import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, StopCircle } from 'lucide-react';
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";

const STREAM_URL = "https://streaming.live365.com/a69618";

export const AudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState([100]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nowPlaying, setNowPlaying] = useState<string>("Loading...");
  const [currentTrack, setCurrentTrack] = useState({ artist: "", title: "" });
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioRef.current = new Audio(STREAM_URL);
    audioRef.current.preload = "none";

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleError = () => {
      setError("Unable to connect to stream");
      setIsLoading(false);
    };
    const handleWaiting = () => setIsLoading(true);
    const handlePlaying = () => {
      setIsLoading(false);
      setNowPlaying("Live Stream");
    };

    const handleMetadata = (e: Event) => {
      const audio = e.target as HTMLAudioElement;
      if ('mediaSession' in navigator && navigator.mediaSession.metadata) {
        const { artist, title } = navigator.mediaSession.metadata;
        setCurrentTrack({
          artist: artist || "",
          title: title || ""
        });
      } else {
        // Fallback for browsers that don't support MediaSession
        const streamTitle = audio.getAttribute('data-stream-title');
        if (streamTitle) {
          const [artist, title] = streamTitle.split(' - ');
          setCurrentTrack({ artist, title });
        }
      }
    };

    if (audioRef.current) {
      audioRef.current.addEventListener("play", handlePlay);
      audioRef.current.addEventListener("pause", handlePause);
      audioRef.current.addEventListener("error", handleError);
      audioRef.current.addEventListener("waiting", handleWaiting);
      audioRef.current.addEventListener("playing", handlePlaying);
      audioRef.current.addEventListener("loadedmetadata", handleMetadata);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener("play", handlePlay);
        audioRef.current.removeEventListener("pause", handlePause);
        audioRef.current.removeEventListener("error", handleError);
        audioRef.current.removeEventListener("waiting", handleWaiting);
        audioRef.current.removeEventListener("playing", handlePlaying);
        audioRef.current.removeEventListener("loadedmetadata", handleMetadata);
        audioRef.current.pause();
      }
    };
  }, []);

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      setError(null);
      setIsLoading(true);
      audioRef.current.play().catch((error) => {
        setError("Unable to play stream");
        setIsLoading(false);
      });
    }
  };

  const stopStream = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setNowPlaying("Loading...");
    setCurrentTrack({ artist: "", title: "" });
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !audioRef.current.muted;
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (value: number[]) => {
    if (!audioRef.current) return;
    const newVolume = value[0];
    setVolume([newVolume]);
    audioRef.current.volume = newVolume / 100;
  };

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-xl p-6 space-y-4 animate-fade-in">
      <div className="flex justify-center items-center space-x-4">
        <button
          onClick={togglePlayPause}
          disabled={!!error}
          className={cn(
            "w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200",
            isPlaying ? "bg-primary text-white" : "bg-secondary hover:bg-primary/10",
            error ? "opacity-50 cursor-not-allowed" : "hover:scale-105"
          )}
        >
          {isLoading ? (
            <div className="w-6 h-6 border-2 border-current rounded-full animate-spin border-t-transparent" />
          ) : isPlaying ? (
            <Pause className="w-8 h-8" />
          ) : (
            <Play className="w-8 h-8 ml-1" />
          )}
        </button>
        <button
          onClick={stopStream}
          className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200",
            "bg-secondary hover:bg-primary/10"
          )}
        >
          <StopCircle className="w-6 h-6" />
        </button>
        <button
          onClick={toggleMute}
          className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200",
            isMuted ? "bg-secondary text-primary" : "bg-secondary hover:bg-primary/10"
          )}
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5" />
          ) : (
            <Volume2 className="w-5 h-5" />
          )}
        </button>
      </div>

      <div className="w-full px-2">
        <Slider
          value={volume}
          onValueChange={handleVolumeChange}
          max={100}
          step={1}
          className="w-full"
        />
      </div>

      {error && (
        <div className="text-red-500 text-center text-sm animate-fade-in">
          {error}
        </div>
      )}

      <div className="text-center text-sm text-gray-500">
        {isPlaying ? (
          <div className="space-y-1">
            <span className="animate-pulse block">Live Streaming</span>
            {currentTrack.artist || currentTrack.title ? (
              <>
                <span className="block font-medium text-gray-700">{currentTrack.artist}</span>
                <span className="block font-medium text-gray-700">{currentTrack.title}</span>
              </>
            ) : (
              <span className="block font-medium text-gray-700">{nowPlaying}</span>
            )}
          </div>
        ) : (
          "Ready to Play"
        )}
      </div>
    </div>
  );
};